import { useState } from "react";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import GoogleSearchAdGroupRow from "./GoogleSearchAdGroupRow";
import GoogleAppAdGroupRow from "./GoogleAppAdGroupRow";
import GoogleAssetsGroupRow from "./GoogleAssetsGroupRow";
import { useFormContext } from "react-hook-form";
import { getFromStatisticsAdsCampaign } from "../../../../../services/adsCampaigns";
import {
  ADS_CAMPAIGN_TYPE_APP_OPTION,
  ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION,
  ADS_CAMPAIGN_TYPE_SEARCH_OPTION,
  ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION,
  GOOGLE,
  getGoogleSearchAdGroupType,
} from "../../../../../utils/constants";
import { useTranslationApp } from "../../../../../lib/i18next";
import TableCell from "../../../../BillingAndPayments/components/TableCell";
import { expanderColumnSx, metricColumnSx } from "../columnWidths";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import { logoPlatform } from "../../../../../utils/string";
import Typography from "@mui/material/Typography";
import { renderMetricValue } from "../../../../../utils/renderMetricValue";
import {
  resolveCampaignLevelStatistics,
  resolveStatisticsMap,
} from "../../../../NewAdsCampaign/components/Common/campaignLevelMetrics";

const GoogleCampaignRow = ({ index, platformData, sortConfig, columns }) => {
  const [open, setOpen] = useState(false);
  const { campaign } = useFormContext();
  const { statistics } = getFromStatisticsAdsCampaign(campaign);

  const { t } = useTranslationApp();

  const objective = platformData?.objective;

  const conversionActions =
    campaign?.platformsData?.[GOOGLE]?.conversionActions;
  const statisticsPlatform = resolveCampaignLevelStatistics({
    raw: statistics[GOOGLE]?.campaign || {},
    platform: GOOGLE,
    conversionActions,
  }).statistics;
  const adsGroupStatisticsById = resolveStatisticsMap(
    statistics[GOOGLE]?.adsGroups,
    GOOGLE,
    conversionActions
  );
  const assetsGroupStatisticsById = resolveStatisticsMap(
    statistics[GOOGLE]?.assetsGroups,
    GOOGLE,
    conversionActions
  );
  const adsGroups = campaign?.adsGroups || [];
  const adsGroupsGoogle = adsGroups.filter(
    (adsGroup) => adsGroup.platform === GOOGLE
  );
  const assetsGroups = campaign?.assetsGroups || [];
  const assetsGroupsGoogle = assetsGroups.filter(
    (assetsGroup) => assetsGroup.platform === GOOGLE
  );

  // Sort ad groups
  const sortedAdsGroups = [...adsGroupsGoogle].sort((a, b) => {
    const aStats = adsGroupStatisticsById[a.id];
    const bStats = adsGroupStatisticsById[b.id];

    if (!aStats || !bStats) return 0;

    if (sortConfig.key) {
      const comparison =
        (aStats[sortConfig.key] || 0) - (bStats[sortConfig.key] || 0);
      return sortConfig.direction === "asc" ? comparison : -comparison;
    }
    return 0;
  });

  // Sort asset groups
  const sortedAssetsGroups = [...assetsGroupsGoogle].sort((a, b) => {
    const aStats = assetsGroupStatisticsById[a.id];
    const bStats = assetsGroupStatisticsById[b.id];

    if (!aStats || !bStats) return 0;

    if (sortConfig.key) {
      const comparison =
        (aStats[sortConfig.key] || 0) - (bStats[sortConfig.key] || 0);
      return sortConfig.direction === "asc" ? comparison : -comparison;
    }
    return 0;
  });

  // Mapeo para renderizar cada celda según la columna
  const cellRenderers = {
    platform: () => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          height: "100%",
          width: "100%",
        }}
      >
        <Avatar
          data-hide-in-pdf-capture
          src={logoPlatform(GOOGLE)}
          variant="square"
          sx={{
            width: 20,
            height: 20,
            ".MuiAvatar-img": { objectFit: "contain" },
          }}
        />
        <Typography variant="body2" sx={{ mt: 0.25 }}>
          {t(GOOGLE)}
        </Typography>
      </Box>
    ),
    costPerMainAction: () =>
      renderMetricValue(
        statisticsPlatform.costPerMainAction,
        "costPerMainAction",
        statisticsPlatform.currency
      ),
  };

  return (
    <>
      <TableRow>
        <TableCell indexRow={index} sx={expanderColumnSx} level={1}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        {columns.map((col) => (
          <TableCell
            key={col.key}
            indexRow={index}
            level={1}
            sx={metricColumnSx(col.width)}
          >
            {cellRenderers[col.key]
              ? cellRenderers[col.key]()
              : renderMetricValue(
                  statisticsPlatform[col.key],
                  col.key,
                  statisticsPlatform.currency
                )}
          </TableCell>
        ))}
      </TableRow>
      {open &&
        (objective === ADS_CAMPAIGN_TYPE_SEARCH_OPTION ||
          objective === ADS_CAMPAIGN_TYPE_APP_OPTION) &&
        sortedAdsGroups.map((adGroup, indexAdGroup) => {
          const adGroupID = adGroup.id;
          const adGroupStatistics = adsGroupStatisticsById[adGroupID];

          if (!adGroupStatistics) return null;

          if (objective === ADS_CAMPAIGN_TYPE_SEARCH_OPTION) {
            const searchAdGroupType = getGoogleSearchAdGroupType(adGroup);
            const isDsa =
              searchAdGroupType === ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION;
            const childStatistics = resolveStatisticsMap(
              isDsa
                ? statistics[GOOGLE]?.webpages?.[adGroupID] || {}
                : statistics[GOOGLE]?.keywords?.[adGroupID] || {},
              GOOGLE,
              conversionActions
            );

            return (
              <GoogleSearchAdGroupRow
                indexCampaign={index}
                indexAdGroup={indexAdGroup}
                adGroupID={adGroupID}
                key={adGroup.id}
                data={adGroupStatistics}
                childStatistics={childStatistics}
                searchAdGroupType={searchAdGroupType}
                sortConfig={sortConfig}
                columns={columns}
              />
            );
          }

          if (objective === ADS_CAMPAIGN_TYPE_APP_OPTION) {
            return (
              <GoogleAppAdGroupRow
                indexCampaign={index}
                indexAdGroup={indexAdGroup}
                adGroupID={adGroupID}
                key={adGroup.id}
                data={adGroupStatistics}
                sortConfig={sortConfig}
                columns={columns}
              />
            );
          }

          return null;
        })}
      {open &&
        objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION &&
        sortedAssetsGroups.map((assetsGroup, indexAssetsGroup) => {
          const assetsGroupID = assetsGroup.id;
          const assetsGroupStatistics =
            assetsGroupStatisticsById[assetsGroupID];

          if (!assetsGroupStatistics) return null;

          return (
            <GoogleAssetsGroupRow
              indexCampaign={index}
              indexAssetsGroup={indexAssetsGroup}
              assetsGroupID={assetsGroupID}
              key={assetsGroup.id}
              data={assetsGroupStatistics}
              sortConfig={sortConfig}
              columns={columns}
            />
          );
        })}
    </>
  );
};

export default GoogleCampaignRow;
